import type { H3Event } from "h3";

export default defineNuxtPlugin((nuxtApp) => {
  const config = useRuntimeConfig();

  // Capture the current event during plugin setup (for SSR)
  let currentEvent: H3Event | undefined;
  if (import.meta.server) {
    // It's important that useRequestEvent() is called directly within the plugin setup
    // for it to reliably get the context.
    try {
      currentEvent = useRequestEvent();
    } catch (e) {
      console.error("Failed to get request event in env plugin:", e);
      // If useRequestEvent() fails here, currentEvent will remain undefined,
      // and server-side cookie operations via this plugin might not work.
    }
  }

  return {
    provide: {
      api: (path: string): string => config.public.api + path,
      delay: (ms: number) => {
        return new Promise((resolve) => setTimeout(resolve, ms));
      },
      localGetItem: (item: string) => {
        if (import.meta.client) {
          return localStorage.getItem(item);
        } else {
          return undefined;
        }
      },
      localSetItem: (item: string, value: string) => {
        if (import.meta.client) {
          localStorage.setItem(item, value);
          return true;
        } else {
          return false;
        }
      },
      /**
       * Retrieves a cookie value.
       * On SSR, reads from the initially captured request event.
       * On client, uses useCookie.
       */
      getItem: (item: string) => {
        if (import.meta.server) {
          if (currentEvent && currentEvent.node.req.headers.cookie) {
            const cookieHeader = currentEvent.node.req.headers.cookie;
            const cookies = cookieHeader.split(";").reduce(
              (acc, cookieStr) => {
                const [name, ...rest] = cookieStr.trim().split("=");
                // Ensure name is decoded if it was encoded
                const raw = decodeURIComponent(rest.join("="));
                // Tolerate legacy JSON-encoded values ("1" -> %221%22) and plain strings
                try {
                  acc[decodeURIComponent(name)] = JSON.parse(raw);
                } catch {
                  acc[decodeURIComponent(name)] = raw;
                }
                return acc;
              },
              {} as Record<string, unknown>,
            );
            return cookies[item] || null;
          }
          // console.warn('[env.plugin getItem SSR] No currentEvent or no cookie header. Cookie reading might fail.');
          return null;
        } else {
          // Client-side: useCookie is fine. Tolerate legacy JSON-encoded values too.
          const cookie = useCookie(item, {
            decodeValue: (v) => {
              try {
                return JSON.parse(decodeURIComponent(v));
              } catch {
                return decodeURIComponent(v);
              }
            },
          });
          return cookie.value || null;
        }
      },
      /**
       * Sets a cookie value.
       * On SSR, sets on the initially captured response event.
       * On client, uses useCookie.
       */
      setItem: (item: string, value: any, options: Record<string, any> = {}) => {
        if (import.meta.server) {
          if (currentEvent) {
            let cookieString = `${encodeURIComponent(item)}=${encodeURIComponent(String(value))}`; // Ensure value is string

            // Apply common cookie options
            if (options.maxAge !== undefined) cookieString += `; Max-Age=${options.maxAge}`;
            cookieString += `; Path=${options.path || "/"}`; // Default path to '/'
            if (options.expires) {
              // Handle Date object or string
              if (options.expires instanceof Date) {
                cookieString += `; Expires=${options.expires.toUTCString()}`;
              } else {
                cookieString += `; Expires=${options.expires}`;
              }
            }
            if (options.secure) cookieString += `; Secure`;
            if (options.httpOnly) cookieString += `; HttpOnly`;
            if (options.sameSite) cookieString += `; SameSite=${options.sameSite}`;

            // Append to existing Set-Cookie headers if any
            let setCookieHeader = currentEvent.node.res.getHeader("Set-Cookie") || [];
            if (typeof setCookieHeader === "string") {
              setCookieHeader = [setCookieHeader];
            } else if (typeof setCookieHeader === "number") {
              // Handle potential number type if a misconfiguration elsewhere set it.
              setCookieHeader = [String(setCookieHeader)];
            }
            // Ensure it's an array before pushing
            if (!Array.isArray(setCookieHeader)) {
              console.error(
                "[env.plugin setItem SSR] setCookieHeader is not an array:",
                setCookieHeader,
              );
              setCookieHeader = []; // Fallback to empty array
            }

            setCookieHeader.push(cookieString);
            currentEvent.node.res.setHeader("Set-Cookie", setCookieHeader);
            return true;
          }
          // console.warn('[env.plugin setItem SSR] No currentEvent. Cookie setting might fail.');
          return false;
        } else {
          // Store the raw value, NOT JSON-encoded. Nuxt's default encoder wraps strings in
          // quotes ("1" -> %221%22), which broke parseInt() when reading group ids back.
          const cookie = useCookie(item, {
            ...options,
            encodeValue: (v) => encodeURIComponent(String(v)),
          });
          cookie.value = value;
          return true;
        }
      },
      customColors: () => {
        return {
          red: "#f87171",
          orange: "#fb923c",
          amber: "#fcd34d",
          yellow: "#fef08a",
          lime: "#d9f99d",
          green: "#bbf7d0",
          emerald: "#a7f3d0",
          teal: "#99f6e4",
          cyan: "#a5f3fc",
          sky: "#bae6fd",
          blue: "#bfdbfe",
          indigo: "#a5b4fc",
          violet: "#c4b5fd",
          purple: "#d8b4fe",
          fuchsia: "#f0abfc",
          pink: "#f9a8d4",
          rose: "#fda4af",
          gray: "#9ca3af",
          stone: "#d6d3d1",
          slate: "#64748b",
        };
      },
      customBadgeColor: (color: string) => {
        return {
          color: {
            white: {
              solid:
                "ring-1 ring-inset ring-gray-300 dark:ring-gray-700 text-gray-900 dark:text-white bg-white dark:bg-gray-900",
            },
            gray: {
              solid:
                "ring-1 ring-inset ring-gray-300 dark:ring-gray-700 text-gray-700 dark:text-gray-200 bg-gray-50 dark:bg-gray-800",
            },
            black: {
              solid: "text-white dark:text-gray-900 bg-gray-900 dark:bg-white",
            },
          },
          variant: {
            solid: `bg-${color}-500 dark:bg-${color}-400 text-white dark:text-gray-900`,
            outline: `text-${color}-500 dark:text-${color}-400 ring-1 ring-inset ring-${color}-500 dark:ring-${color}-400`,
            soft: `bg-${color}-50 dark:bg-${color}-400 dark:bg-opacity-10 text-${color}-500 dark:text-${color}-400`,
            subtle: `bg-${color}-50 dark:bg-${color}-400 dark:bg-opacity-10 text-${color}-500 dark:text-${color}-400 ring-1 ring-inset ring-${color}-500 dark:ring-{color}-400 ring-opacity-25 dark:ring-opacity-2`,
          },
        };
      },
    },
  };
});
